import { useState, useMemo, useCallback } from 'react';
import { Material, ProductTemplate, TemplateComponent, FinancialConfig } from '../types';

export interface UseRecipeEditorProps {
  userId: string;
  materials: Material[];
  config: FinancialConfig;
  recipes: ProductTemplate[];
}

export function useRecipeEditor({ userId, materials, config, recipes }: UseRecipeEditorProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRecipe, setEditingRecipe] = useState<Partial<ProductTemplate> | null>(null);

  const filteredRecipes = useMemo(() => {
    return recipes.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [recipes, searchTerm]);

  const calculateRecipeCosts = useCallback((template: Partial<ProductTemplate>) => {
    const items = template.items || [];
    const materialCost = items.reduce((acc, item) => {
      const material = materials.find(m => m.id === item.materialId);
      if (!material) return acc;
      
      const yieldAmt = material.yieldAmount || 1;
      const unitCost = material.pricePaid / yieldAmt;
      return acc + (unitCost * item.quantity);
    }, 0);

    const laborCost = (config.baseHourValue / 60) * (template.executionTimeMinutes || 0);
    const totalCost = materialCost + laborCost;

    return { materialCost, laborCost, totalCost };
  }, [materials, config.baseHourValue]);

  const handleCreateNew = useCallback((): void => {
    setEditingRecipe({
      userId,
      name: '',
      executionTimeMinutes: 30,
      items: [],
      createdAt: new Date().toISOString()
    });
    setIsModalOpen(true);
  }, [userId]);

  const handleEdit = useCallback((recipe: ProductTemplate): void => {
    setEditingRecipe({ ...recipe });
    setIsModalOpen(true);
  }, []);

  const handleAddItem = useCallback((): void => {
    const firstMaterial = materials[0];
    if (!firstMaterial) {
      alert('Cadastre materiais primeiro!');
      return;
    }

    setEditingRecipe(prev => {
      if (!prev) return null;
      const newItem: TemplateComponent = {
        id: crypto.randomUUID(),
        templateId: prev.id || '',
        materialId: firstMaterial.id || '',
        quantity: 1
      };
      return {
        ...prev,
        items: [...(prev.items || []), newItem]
      };
    });
  }, [materials]);

  const updateItem = useCallback(<K extends keyof TemplateComponent>(
    id: string,
    field: K,
    value: TemplateComponent[K]
  ): void => {
    setEditingRecipe(prev => {
      if (!prev) return null;
      return {
        ...prev,
        items: prev.items?.map(item => 
          item.id === id ? { ...item, [field]: value } : item
        )
      };
    });
  }, []);

  const removeItem = useCallback((id: string): void => {
    setEditingRecipe(prev => {
      if (!prev) return null;
      return {
        ...prev,
        items: prev.items?.filter(item => item.id !== id)
      };
    });
  }, []);

  const handleSave = useCallback((onSaveRecipe: (recipe: ProductTemplate) => void): void => {
    setEditingRecipe(prev => {
      if (!prev) return null;
      if (!prev.name) {
        alert('Dê um nome à receita!');
        return prev;
      }

      const recipeToSave: ProductTemplate = {
        id: prev.id || crypto.randomUUID(),
        userId: prev.userId || userId,
        name: prev.name,
        category: prev.category || 'recipe',
        description: prev.description,
        executionTimeMinutes: prev.executionTimeMinutes ?? 30,
        items: prev.items || [],
        createdAt: prev.createdAt || new Date().toISOString(),
        attachments: prev.attachments
      };

      onSaveRecipe(recipeToSave);
      setIsModalOpen(false);
      return prev;
    });
  }, [userId]);

  return {
    searchTerm,
    setSearchTerm,
    isModalOpen,
    setIsModalOpen,
    editingRecipe,
    setEditingRecipe,
    filteredRecipes,
    calculateRecipeCosts,
    handleCreateNew,
    handleEdit,
    handleAddItem,
    updateItem,
    removeItem,
    handleSave
  };
}
